/**
 * Bezettingsgraad's addresses: Overzicht at `/apps/bezetting`, every other
 * tab at `/apps/bezetting/<tab>`, and each page's selections (a crane, a
 * client, a region, a period) in the query string, so a link opens the same
 * view. A client id lives in the query (`klant`), never in the path.
 *
 * Old and mistyped addresses land on a page instead of breaking: an earlier
 * build put the tab in the query (`?tab=kranen`), and a tab that no longer
 * exists (`methode`) or never did opens Overzicht, keeping the selections.
 */

import {
  BEZETTING_DEFAULT_TAB,
  BEZETTING_PATH,
  BEZETTING_TABS,
  type BezettingTab,
} from "./contract";

/** The query key an earlier build kept the tab in. */
const LEGACY_TAB_PARAM = "tab";

export function isBezettingTab(value: unknown): value is BezettingTab {
  return typeof value === "string" && (BEZETTING_TABS as readonly string[]).includes(value);
}

/** A tab's path: Overzicht at the app's own path, the others under it. */
export function bezettingTabPath(tab: BezettingTab): string {
  return tab === BEZETTING_DEFAULT_TAB ? BEZETTING_PATH : `${BEZETTING_PATH}/${tab}`;
}

/** A query as a location's search: "?kraan=12", or "" without one. */
export function searchOf(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Where a location sends someone instead, keeping the selections. */
export interface BezettingRedirect {
  pathname: string;
  search: string;
}

export type BezettingRoute =
  | { readonly kind: "page"; readonly tab: BezettingTab }
  | { readonly kind: "redirect"; readonly to: BezettingRedirect };

/**
 * What an app address shows: a tab, or where to go instead (a replace, so
 * Back skips it).
 *
 * - `/apps/bezetting` is Overzicht; with `?tab=<x>` a known tab moves into
 *   the path, anything else is dropped.
 * - `/apps/bezetting/<tab>` is that tab; a `?tab=` beside it is dropped.
 * - `/apps/bezetting/overzicht`, `/apps/bezetting/methode` and any unknown
 *   tab go to `/apps/bezetting`.
 *
 * @param pathTab the `:tab` segment, undefined at the app's own path
 * @param search the location's search, with or without its "?"
 */
export function resolveBezettingRoute(pathTab: string | undefined, search: string): BezettingRoute {
  const params = new URLSearchParams(search);
  const legacyTab = params.get(LEGACY_TAB_PARAM);
  params.delete(LEGACY_TAB_PARAM);
  const rest = searchOf(params);

  if (pathTab === undefined) {
    if (legacyTab === null) {
      return { kind: "page", tab: BEZETTING_DEFAULT_TAB };
    }
    const tab = isBezettingTab(legacyTab) ? legacyTab : BEZETTING_DEFAULT_TAB;
    return { kind: "redirect", to: { pathname: bezettingTabPath(tab), search: rest } };
  }
  if (isBezettingTab(pathTab) && pathTab !== BEZETTING_DEFAULT_TAB) {
    return legacyTab === null
      ? { kind: "page", tab: pathTab }
      : { kind: "redirect", to: { pathname: bezettingTabPath(pathTab), search: rest } };
  }
  return { kind: "redirect", to: { pathname: BEZETTING_PATH, search: rest } };
}
